import { ChatTimeline } from '@/components/chat/components/ChatTimeline';
import { cn } from '@/lib/cn';
import { resolveTimelineErrorDisplay } from '@/utils/chatErrors';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { memo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { SBSStreamState } from '../hooks/useEvidenceSBSStreams';
import type { EvidenceSBSCandidate } from '../types';
import { EvidenceSBSCandidateContentSkeleton } from './EvidenceSBSSkeleton';

interface Props {
  index: number;
  candidate: EvidenceSBSCandidate;
  streamState: SBSStreamState;
  onConfirm: (candidate: EvidenceSBSCandidate) => void;
  confirmDisabled: boolean;
  minimal?: boolean;
}

function EvidenceSBSCandidateComponent({
  index,
  candidate,
  streamState,
  onConfirm,
  confirmDisabled,
  minimal = false,
}: Props) {
  const intl = useIntl();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const latestGroupRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  // EVI-1403 #1 (initial load): `makeStreamingConversation` seeds a
  // `reasoning_item` placeholder (`backendIndex: -1`) the instant a stream
  // starts. A naive `timeline.length > 0` check therefore flips to true
  // immediately, skipping the skeleton, and ChatTimeline renders only the
  // placeholder (a small spinner dot) over an otherwise blank card — the
  // exact "empty card" look the skeleton was meant to replace. Treat a
  // placeholder-only timeline as still empty so the skeleton stays mounted
  // until the first real chunk lands.
  const hasContent = streamState.conversation.timeline.some(
    (entry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
  );

  // EVI-1403 #2 (revised, no-rerender): minimal and non-minimal share a
  // SINGLE return tree so `ChatTimeline` (and the Streamdown-rendered
  // KeyPointsBlock / ReferencesSection inside) keeps the same React
  // fiber across the comparison→minimal toggle. Previously minimal
  // returned a different structure (`<div>` vs `<Card>`), which made
  // React reconcile the inner subtree as a fresh mount: half-loaded
  // key points re-animated from scratch on every Prefer click. Now the
  // chrome (Option header, Prefer button) is conditionally rendered as
  // siblings, and the Card's visual styling is toggled via classes —
  // but `ChatTimeline` is in the same JSX position in both modes, so
  // its DOM, scroll position, Streamdown parsing state, and any
  // in-flight CSS animations all persist across the click.
  return (
    <Card
      // EVI-1403 #3 (expanded): card now fills its grid cell (`h-full`)
      // instead of being pinned to 65vh, so heading + cards + Skip strip +
      // each card's "Prefer this response" footer all fit within the
      // overlay's viewport. Internal content scrolls inside the flex-1 area
      // below; min-h is reduced so the layout still works on short viewports.
      className={cn(
        'relative flex h-full flex-col overflow-hidden transition',
        // Minimal mode strips Card chrome so the chosen card visually
        // takes over the chat area (no border, no shadow, no rounded
        // corners, transparent bg). Same look as the previous separate
        // minimal-view div, but preserves the React instance + DOM
        // identity so the streaming Markdown subtree doesn't remount.
        minimal
          ? 'min-h-[200px] rounded-none border-0 bg-transparent shadow-none'
          : 'min-h-[300px] hover:shadow-lg',
        !minimal && candidate.confirmed && 'border-2 border-validation-info'
      )}
    >
      {!minimal && (
        <div className="shrink-0 border-b px-4 py-2">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              id="NxWYm3JcW9"
              defaultMessage="Option {index}"
              values={{ index: index + 1 }}
            />
          </TypographyCaption>
        </div>
      )}

      <div className="relative min-h-0 flex-1 [&_[data-testid=thinking-block-refine-sources]]:hidden">
        {hasContent ? (
          <ChatTimeline
            ref={scrollAreaRef}
            conversation={streamState.conversation}
            isStreaming={streamState.isStreaming}
            sessionId={null}
            isFullScreenEvidence
            latestGroupRef={latestGroupRef}
            timelineRef={timelineRef}
          />
        ) : (
          // EVI-1403 #1: replaced the "tiny spinner in empty card" loading
          // state with a content-shaped skeleton (key points block + content
          // lines) so the user sees a believable "your answer is coming"
          // shape from the first frame instead of a blank card. Only the
          // inner content area is replaced — the card's header + footer are
          // the real components, so when content arrives the layout doesn't
          // shift.
          <EvidenceSBSCandidateContentSkeleton />
        )}

        {streamState.error && (
          <div className="absolute inset-x-0 bottom-0 bg-surface px-4 py-3">
            <TypographyP2 className="text-validation-destructive">
              {resolveTimelineErrorDisplay(streamState.error, (descriptor) =>
                intl.formatMessage(descriptor)
              )}
            </TypographyP2>
          </div>
        )}
      </div>

      {!minimal && (
        <div className="shrink-0 border-t bg-surface px-4 py-3">
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            disabled={confirmDisabled || candidate.confirmed}
            onClick={() => onConfirm(candidate)}
          >
            {candidate.confirmed ? (
              <FormattedMessage id="FpkP6En4XX" defaultMessage="Thanks for improving" />
            ) : (
              <FormattedMessage id="gPIo/zmFFW" defaultMessage="Prefer this response" />
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}

export const EvidenceSBSCandidateCard = memo(EvidenceSBSCandidateComponent);
